import {
  toggleCollapsible,
  ToggleCollapsibleEvent
} from "../../../Shared/Collapsible/collapsible";
import {
  getRequiredDatasetAttribute,
  getRequiredElement
} from "../../../Shared/Utilities/attributes";

const PAGE_SIZE = 10;
const COLLAPSIBLE_TRANSITION_DURATION_MS = 100;

export type DropdownSelectReturnValue = {
  toggleOption: (option: string) => void;
};

/**
 * Initializes a dropdown select component.
 * @param dropdownSelect The root element of the dropdown select.
 * @returns An object containing methods for interacting with the dropdown.
 */
export const initializeDropdownSelect = (
  dropdownSelect: HTMLElement
): DropdownSelectReturnValue => {
  const id = dropdownSelect.id.substring("ds-".length);

  const content = getRequiredElement<HTMLElement>(
    dropdownSelect,
    "label",
    "Dropdown Select content missing"
  );
  const label = getRequiredElement<HTMLElement>(
    dropdownSelect,
    "[data-label]",
    "Dropdown Select label missing"
  );
  const select = getRequiredElement<HTMLSelectElement>(
    dropdownSelect,
    "select",
    "Dropdown Select select missing"
  );
  const listbox = getRequiredElement<HTMLElement>(
    dropdownSelect,
    "[role='listbox']",
    "Dropdown Select listbox missing"
  );
  const openCollapsibleButton = getRequiredElement<HTMLSelectElement>(
    dropdownSelect,
    `[aria-controls="ds-collapsible-${id}"]`,
    "Dropdown Select openCollapsibleButton missing"
  );
  const collapsible = getRequiredElement<HTMLSelectElement>(
    dropdownSelect,
    "[data-collapsible]",
    "Dropdown Select collapsible missing"
  );
  const placeholder = getRequiredElement<HTMLElement>(
    dropdownSelect,
    "[data-placeholder]",
    "Dropdown Select placeholder missing"
  );
  const deselectButton = getRequiredElement<HTMLElement>(
    dropdownSelect,
    "[data-deselect]",
    "Dropdown Select deselect button missing"
  );

  const placeholderText = getRequiredDatasetAttribute(
    placeholder,
    "placeholder",
    "Dropdown Select placeholder text missing"
  );

  const selectOptions = Array.from(select.options);
  const options: HTMLElement[] = Array.from(
    listbox.querySelectorAll<HTMLElement>("[role='option']")
  );

  options.forEach(option => {
    option.addEventListener("click", _ => {
      toggleOption(option);
    });
  });

  let focusTimer: number;
  let focusIndex = select.selectedIndex !== -1 ? select.selectedIndex : 0;

  /**
   * Toggles option classes based on selection state.
   */
  const toggleOptionClasses = (option: HTMLElement, selected: boolean) => {
    const activeClasses = (option.dataset.activeClass ?? "").split(" ").filter(Boolean);
    const inactiveClasses = (option.dataset.inactiveClass ?? "")
      .split(" ")
      .filter(Boolean);

    if (selected) {
      option.classList.remove(...inactiveClasses);
      option.classList.add(...activeClasses);
    } else {
      option.classList.remove(...activeClasses);
      option.classList.add(...inactiveClasses);
    }
  };

  /**
   * Selects an option, updates the dropdown, and triggers a change event.
   */
  const toggleOption = (option: HTMLElement) => {
    const previousOption = options.find(opt => opt.dataset.value === select.value);
    if (previousOption) {
      toggleOptionClasses(previousOption, false);
    }

    select.value = option.dataset.value ?? "";
    toggleOptionClasses(option, true);
    select.dispatchEvent(new Event("change"));
  };

  /**
   * Updates focus to a new option.
   */
  const updateFocusedOption = (newFocusIndex: number) => {
    options[focusIndex]?.blur();
    focusTimer = window.setTimeout(
      () => options[newFocusIndex]?.focus(),
      COLLAPSIBLE_TRANSITION_DURATION_MS
    );
    focusIndex = newFocusIndex;
  };

  const openClassesStr = content.dataset.openClass ?? "";
  const closedClassesStr = content.dataset.closedClass ?? "";

  const openClasses = openClassesStr.split(" ").filter(_class => !!_class);
  const closedClasses = closedClassesStr.split(" ").filter(_class => !!_class);

  const getSelectedIndex = () => {
    if (select.selectedOptions.length > 0) {
      return selectOptions.findIndex(selectOption => selectOption.selected);
    }

    return -1;
  };

  /**
   * Handles dropdown collapsible toggle behavior.
   */
  const onToggleCollapsible = (isOpening: boolean, event?: Event) => {
    if (isOpening) {
      content.classList.remove(...closedClasses);
      content.classList.add(...openClasses);
      dropdownSelect.dataset.isOpen = "true";

      if (
        event &&
        event.type === "click" &&
        event.currentTarget === openCollapsibleButton
      ) {
        const selectedIndex = getSelectedIndex();

        updateFocusedOption(selectedIndex === -1 ? 0 : selectedIndex);
      }
    } else {
      clearTimeout(focusTimer);

      content.classList.remove(...openClasses);
      content.classList.add(...closedClasses);
      dropdownSelect.dataset.isOpen = "false";

      if (event instanceof KeyboardEvent && event.key !== "Tab") {
        openCollapsibleButton.focus();
      }
    }
  };

  collapsible.addEventListener("ani:toggle", (e: Event) => {
    const event = e as ToggleCollapsibleEvent;
    onToggleCollapsible(event.detail.isOpening, event.detail.originalEvent);
  });

  /**
   * Opens the collapsible dropdown.
   */
  const openCollapsible = (event: Event) =>
    toggleCollapsible(collapsible, { event, forceState: true });

  /**
   * Closes the collapsible dropdown.
   */
  const closeCollapsible = (event: Event) =>
    toggleCollapsible(collapsible, { event, forceState: false });

  openCollapsibleButton.addEventListener("keydown", (e: KeyboardEvent) => {
    if (["ArrowDown", "ArrowUp", "Enter", " ", "Home", "End"].includes(e.key)) {
      updateFocusedOption(
        e.key === "Home" ? 0 : e.key === "End" ? options.length - 1 : focusIndex
      );
      openCollapsible(e);
      e.preventDefault();
    }
  });

  /**
   * Handles keyboard navigation and selection inside the listbox.
   */
  listbox.addEventListener("keydown", (e: KeyboardEvent) => {
    const keyActions: Record<string, () => void> = {
      ArrowUp: () => updateFocusedOption(Math.max(0, focusIndex - 1)),
      PageUp: () => updateFocusedOption(Math.max(0, focusIndex - PAGE_SIZE)),
      ArrowDown: () => updateFocusedOption(Math.min(options.length - 1, focusIndex + 1)),
      PageDown: () =>
        updateFocusedOption(Math.min(options.length - 1, focusIndex + PAGE_SIZE)),
      Home: () => updateFocusedOption(0),
      End: () => updateFocusedOption(options.length - 1),
      Tab: () => {
        const option = options[focusIndex];
        if (option) {
          const selectOption = selectOptions.find(
            selectOption => option.dataset.value === selectOption.value
          );

          if (selectOption && !selectOption.selected) {
            toggleOption(option);
          }
        }

        closeCollapsible(e);
      },
      Enter: () => {
        const option = options[focusIndex];
        if (option) {
          toggleOption(option);
        }
      }
    };
    if (keyActions[e.key]) {
      keyActions[e.key]();
      e.preventDefault();
    }
  });

  /**
   * Update the placeholder / label when the select is changed
   */
  select.addEventListener("change", _ => {
    const selectedOption = select.selectedOptions[0];

    if (selectedOption) {
      label.innerHTML = `${select.selectedOptions[0].innerText}`;
      dropdownSelect.dataset.hasSelectedOption = "true";
    }
  });

  /**
   * Prevent form submit on enter
   */
  select.addEventListener("keydown", e => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  });

  /**
   * Initializes deselect button behavior.
   */
  deselectButton.addEventListener("click", (event: Event) => {
    event.stopPropagation();
    updateFocusedOption(0);
    placeholder.innerHTML = placeholderText;
    dropdownSelect.dataset.hasSelectedOption = "false";
    select.selectedIndex = -1;
    options.forEach(option => toggleOptionClasses(option, false));
  });

  return {
    toggleOption: optionValue =>
      options.find(opt => opt.dataset.value === optionValue)?.click()
  };
};

/**
 * Automatically initializes all dropdown selects on the page.
 */
export const initializeAllDropdownSelects = () => {
  document.querySelectorAll<HTMLElement>("[data-dropdown-select]").forEach(element => {
    if (element.dataset.dropdownSelectAutoInit !== "false") {
      initializeDropdownSelect(element);
    }
  });
};
